import { motion } from "framer-motion";
import { FEATURES } from "./catalog";

const loop = { repeat: Infinity, ease: "easeInOut" };

function Stage({ children }) {
  return (
    <div className="relative h-72 sm:h-80 border border-aura-border bg-aura-surface/50 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(247,37,133,0.18),transparent_45%),radial-gradient(circle_at_80%_70%,rgba(76,201,240,0.16),transparent_42%)]" />
      <div className="relative h-full p-5">{children}</div>
    </div>
  );
}

function Figure({ dressed }) {
  return (
    <svg viewBox="0 0 120 180" className="h-full w-auto mx-auto" aria-hidden="true">
      <ellipse cx="60" cy="172" rx="28" ry="4" fill="#000" opacity="0.35" />
      <circle cx="60" cy="28" r="16" fill="#2A2A30" stroke="#E4E4E7" strokeWidth="1.5" />
      <path d="M46 24c2-12 26-12 28 0" fill="#3F3F46" />
      <path d="M44 50c-10 8-14 28-10 48l8-6 6 54h8l4-36 4 36h8l6-54 8 6c4-20 0-40-10-48-6 8-22 8-28 0z" fill="#27272A" stroke="#A1A1AA" strokeWidth="1.2" />
      <path d="M52 118l-2 48M68 118l2 48" stroke="#52525B" strokeWidth="7" strokeLinecap="round" />
      {dressed && (
        <motion.path
          d="M40 54c-8 6-12 22-8 40l10-4 4 28h28l4-28 10 4c4-18 0-34-8-40-8 10-32 10-40 0z"
          fill="#F72585"
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: [1, 1, 0.2, 1], y: [0, 0, -18, 0] }}
          transition={{ duration: 4.6, times: [0, 0.62, 0.78, 1], ...loop }}
        />
      )}
    </svg>
  );
}

function JacketChip() {
  return (
    <svg viewBox="0 0 64 64" className="w-full h-full" aria-hidden="true">
      <rect width="64" height="64" rx="6" fill="#18181B" />
      <path d="M16 22l8 6 8-8 8 8 8-6v26H16z" fill="#F72585" />
      <path d="M32 20v28" stroke="#fff" strokeOpacity="0.7" />
    </svg>
  );
}

function ShoeMark() {
  return (
    <svg viewBox="0 0 88 48" className="w-full h-12" aria-hidden="true">
      <path d="M6 30c10-16 28-18 44-12 12 4 22 2 32 8v10H12c-8 0-10-3-6-6z" fill="#F4F4F5" />
      <path d="M10 36h70" stroke="#F72585" strokeWidth="3" strokeLinecap="round" />
      <circle cx="22" cy="28" r="2" fill="#4CC9F0" />
    </svg>
  );
}

function HeadphoneMark() {
  return (
    <svg viewBox="0 0 72 48" className="w-14 h-10" aria-hidden="true">
      <path d="M14 28c0-14 10-22 22-22s22 8 22 22" fill="none" stroke="#F4F4F5" strokeWidth="4" />
      <rect x="8" y="24" width="10" height="16" rx="3" fill="#4CC9F0" />
      <rect x="54" y="24" width="10" height="16" rx="3" fill="#F72585" />
    </svg>
  );
}

function BagMark() {
  return (
    <svg viewBox="0 0 64 64" className="w-full h-full" aria-hidden="true">
      <rect x="12" y="22" width="40" height="32" rx="4" fill="#E4E4E7" />
      <path d="M22 22v-4a10 10 0 0 1 20 0v4" fill="none" stroke="#F72585" strokeWidth="3" />
    </svg>
  );
}

export function CompareDemo() {
  const rows = [
    { store: "eBay", price: "$119", best: true, mark: <HeadphoneMark /> },
    { store: "Partner store", price: "$128", best: false, mark: <HeadphoneMark /> },
    { store: "Shoptosa seller", price: "$134", best: false, mark: <HeadphoneMark /> },
  ];
  return (
    <Stage>
      <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-3">Same item</div>
      <div className="space-y-2">
        {rows.map((row, i) => (
          <motion.div
            key={row.store}
            initial={{ x: 0, opacity: 1 }}
            animate={{ x: [18, 0] }}
            transition={{ delay: 0.15 + i * 0.12, duration: 0.45 }}
            className={`flex items-center gap-3 px-3 py-2 border text-sm ${row.best ? "border-aura-accent bg-aura-accent/10" : "border-aura-border bg-aura-bg/70"}`}
          >
            <span className="w-14 h-10 bg-aura-bg border border-aura-border flex items-center justify-center shrink-0">{row.mark}</span>
            <span className="flex-1">{row.store}</span>
            <span className="font-medium">{row.price}</span>
          </motion.div>
        ))}
      </div>
    </Stage>
  );
}

export function SearchDemo() {
  const text = "white leather sneakers";
  return (
    <Stage>
      <div className="border border-aura-border bg-aura-bg/80 px-4 py-3 text-sm">
        {text.split("").map((ch, i) => (
          <motion.span key={`${ch}-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 + i * 0.035 }}>
            {ch}
          </motion.span>
        ))}
        <motion.span className="inline-block w-px h-4 bg-aura-cyan ml-0.5 align-middle" animate={{ opacity: [1, 0, 1] }} transition={{ duration: 1, ...loop }} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {["Pair A", "Pair B", "Pair C"].map((name, i) => (
          <motion.div
            key={name}
            initial={{ opacity: 1, y: 0 }}
            animate={{ y: [12, 0] }}
            transition={{ delay: 0.8 + i * 0.12, duration: 0.4 }}
            className="border border-aura-border bg-aura-bg/80 p-2"
          >
            <div className="h-16 flex items-end justify-center bg-[radial-gradient(circle_at_50%_80%,rgba(76,201,240,0.25),transparent_70%)]">
              <ShoeMark />
            </div>
            <div className="mt-1 text-[11px] text-aura-muted">{name}</div>
          </motion.div>
        ))}
      </div>
    </Stage>
  );
}

export function TryOnDemo() {
  return (
    <Stage>
      <div className="grid grid-cols-[1fr_auto_1fr] gap-2 h-full items-stretch">
        <div className="border border-aura-border bg-aura-bg/70 p-2 flex flex-col min-w-0">
          <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-1">Your photo</div>
          <div className="flex-1 min-h-0"><Figure dressed={false} /></div>
        </div>
        <div className="flex flex-col items-center justify-center gap-2 w-14">
          <motion.div
            className="w-12 h-12 border border-aura-accent bg-aura-bg"
            animate={{ y: [8, 0, 0, 8], opacity: [0.4, 1, 1, 0.4] }}
            transition={{ duration: 4.8, ...loop }}
          >
            <JacketChip />
          </motion.div>
          <motion.span className="text-aura-cyan text-lg" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.6, ...loop }}>→</motion.span>
        </div>
        <div className="border border-aura-cyan/50 bg-aura-bg/70 p-2 flex flex-col min-w-0 relative overflow-hidden">
          <div className="text-[10px] tracking-luxe uppercase text-aura-cyan mb-1">Preview</div>
          <div className="flex-1 min-h-0"><Figure dressed /></div>
          <motion.div
            className="absolute inset-x-0 h-8 bg-gradient-to-b from-transparent via-aura-cyan/30 to-transparent pointer-events-none"
            animate={{ top: ["-10%", "110%"] }}
            transition={{ duration: 2.4, ...loop, ease: "linear" }}
          />
        </div>
      </div>
    </Stage>
  );
}

export function LiveDemo() {
  return (
    <Stage>
      <div className="h-full border border-aura-border bg-aura-bg/70 relative overflow-hidden">
        <div className="absolute top-3 left-3 z-10 rounded-full bg-aura-accent text-white text-[10px] font-semibold tracking-luxe uppercase px-2.5 py-0.5 inline-flex items-center gap-1.5">
          <motion.span className="w-1.5 h-1.5 rounded-full bg-white" animate={{ opacity: [1, 0.2, 1] }} transition={{ duration: 1.1, ...loop }} />
          Live
        </div>
        <div className="absolute inset-0 flex items-end justify-center pb-8">
          <Figure dressed />
        </div>
          <motion.div
            className="absolute bottom-3 right-3 bg-white text-black text-xs font-semibold px-3 py-1.5"
            initial={{ opacity: 1, y: 0 }}
            animate={{ y: [8, 0, 0, 8] }}
            transition={{ duration: 4.2, ...loop }}
          >
          Jacket · $89
        </motion.div>
      </div>
    </Stage>
  );
}

export function CrosslistDemo() {
  const shops = ["eBay", "Etsy", "Poshmark", "Mercari"];
  return (
    <Stage>
      <div className="flex items-center gap-4 h-full">
        <div className="w-24 shrink-0 border border-aura-border bg-aura-bg p-2">
          <div className="h-16 flex items-center justify-center"><BagMark /></div>
          <div className="text-[11px] mt-1">One listing</div>
        </div>
        <svg viewBox="0 0 40 120" className="w-8 h-28 shrink-0" aria-hidden="true">
          {[18, 46, 74, 102].map((y, i) => (
            <motion.path
              key={y}
              d={`M2 60 C 18 60, 18 ${y}, 38 ${y}`}
              fill="none"
              stroke="#4CC9F0"
              strokeWidth="1.5"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: [0, 1, 1] }}
              transition={{ duration: 2.2, delay: i * 0.15, ...loop }}
            />
          ))}
        </svg>
        <div className="flex-1 grid grid-cols-1 gap-2">
          {shops.map((name, i) => (
            <motion.div
              key={name}
              className="px-3 py-1.5 border text-sm bg-aura-bg/80"
              animate={{ borderColor: ["#27272A", "#4CC9F0", "#27272A"], opacity: [0.45, 1, 0.45] }}
              transition={{ duration: 2.8, delay: 0.3 + i * 0.15, ...loop }}
            >
              {name}
            </motion.div>
          ))}
        </div>
      </div>
    </Stage>
  );
}

export function DelistDemo() {
  const shops = ["eBay", "Etsy", "Poshmark", "Mercari"];
  return (
    <Stage>
      <div className="text-[10px] tracking-luxe uppercase text-aura-accent mb-3">Sold on eBay</div>
      <div className="grid grid-cols-2 gap-2">
        {shops.map((name, i) => (
          <motion.div
            key={name}
            animate={{ opacity: name === "eBay" ? 1 : [1, 1, 0.28] }}
            transition={{ duration: 2.4, delay: 0.35 + i * 0.12, ...loop }}
            className={`px-3 py-3 border text-sm ${name === "eBay" ? "border-aura-accent bg-aura-accent/10" : "border-aura-border"}`}
          >
            {name}
            {name !== "eBay" && <div className="text-[10px] text-aura-muted mt-1">Removed</div>}
          </motion.div>
        ))}
      </div>
    </Stage>
  );
}

export function PhotoDemo() {
  return (
    <Stage>
      <div className="grid grid-cols-2 gap-3 h-full">
        <div className="border border-aura-border bg-[#1c1410] flex flex-col justify-end p-3">
          <div className="flex-1 flex items-center justify-center">
            <BagMark />
          </div>
          <div className="text-[11px] text-aura-muted">Flat lay</div>
        </div>
        <div className="relative border border-aura-cyan/50 overflow-hidden bg-[#0c1014]">
          <motion.div
            className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(76,201,240,0.45),transparent_55%),linear-gradient(#141418,#0b0b0e)]"
            initial={{ clipPath: "inset(0 100% 0 0)" }}
            animate={{ clipPath: ["inset(0 100% 0 0)", "inset(0 0% 0 0)", "inset(0 0% 0 0)"] }}
            transition={{ duration: 3.2, ...loop }}
          />
          <div className="relative h-full flex flex-col justify-end p-3">
            <div className="flex-1 flex items-center justify-center"><BagMark /></div>
            <div className="text-[11px]">Studio shot</div>
          </div>
        </div>
      </div>
    </Stage>
  );
}

export function MarketsDemo() {
  const names = ["eBay", "Etsy", "Poshmark", "Mercari", "Amazon", "Shopify"];
  return (
    <Stage>
      <div className="flex items-center gap-4 h-full">
        <div className="w-20 h-20 border border-aura-border bg-aura-bg flex items-center justify-center shrink-0">
          <BagMark />
        </div>
        <div className="flex flex-wrap gap-2 content-center">
          {names.map((name, i) => (
            <motion.span
              key={name}
              initial={{ opacity: 1, y: 0 }}
              animate={{ y: [10, 0] }}
              transition={{ delay: i * 0.08, duration: 0.35 }}
              className="px-3 py-2 border border-aura-border text-sm bg-aura-bg/70"
            >
              {name}
            </motion.span>
          ))}
        </div>
      </div>
    </Stage>
  );
}

export function GraphDemo() {
  const offers = [
    { d: "M78 100 C 110 100, 130 46, 168 46", delay: 0 },
    { d: "M78 100 C 120 100, 140 110, 176 110", delay: 0.2 },
    { d: "M78 100 C 110 100, 130 164, 164 164", delay: 0.4 },
  ];
  return (
    <Stage>
      <svg viewBox="0 0 320 200" className="w-full h-full" aria-hidden="true">
        {offers.map((edge) => (
          <motion.path
            key={edge.d}
            d={edge.d}
            fill="none"
            stroke="#4CC9F0"
            strokeWidth="1.6"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: [0, 1, 1, 0] }}
            transition={{ duration: 3.4, delay: edge.delay, ...loop }}
          />
        ))}
        <circle cx="60" cy="100" r="26" fill="#121214" stroke="#F72585" strokeWidth="1.6" />
        <text x="60" y="104" textAnchor="middle" fill="white" fontSize="11">Item</text>
        {[[188, 46], [198, 110], [184, 168]].map(([x, y], i) => (
          <g key={y}>
            <motion.circle
              cx={x}
              cy={y}
              r="16"
              fill="#121214"
              stroke="#4CC9F0"
              animate={{ opacity: [0.35, 1, 1] }}
              transition={{ duration: 3.4, delay: 0.4 + i * 0.2, ...loop }}
            />
            <text x={x} y={y + 3} textAnchor="middle" fill="#A1A1AA" fontSize="8">Offer</text>
          </g>
        ))}
      </svg>
    </Stage>
  );
}

export function ClickDemo() {
  return (
    <Stage>
      <svg viewBox="0 0 360 180" className="w-full h-full" aria-hidden="true">
        <motion.path
          d="M70 90 H290"
          fill="none"
          stroke="#4CC9F0"
          strokeWidth="1.5"
          strokeDasharray="6 6"
          animate={{ strokeDashoffset: [0, -48] }}
          transition={{ duration: 1.6, ...loop, ease: "linear" }}
        />
        <motion.circle cx="70" cy="90" r="5" fill="#F72585" animate={{ cx: [70, 180, 290, 70] }} transition={{ duration: 3.6, ...loop, ease: "easeInOut" }} />
      </svg>
      <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 flex justify-between text-sm pointer-events-none">
        {["Shopper", "Tracked link", "Store"].map((label) => (
          <span key={label} className="px-3 py-2 border border-aura-border bg-aura-bg/90">{label}</span>
        ))}
      </div>
    </Stage>
  );
}

export function AllDemo() {
  const items = FEATURES.filter((item) => item.slug !== "all-capabilities");
  return (
    <Stage>
      <div className="grid grid-cols-3 gap-2">
        {items.map((item, i) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.slug}
              initial={{ opacity: 1, y: 0 }}
              animate={{ y: [8, 0] }}
              transition={{ delay: i * 0.04, duration: 0.35 }}
              className="border border-aura-border bg-aura-bg/70 px-2 py-2 flex items-center gap-2 min-w-0"
            >
              <Icon className="w-3.5 h-3.5 shrink-0 text-aura-cyan" strokeWidth={1.5} />
              <span className="text-[11px] truncate">{item.title}</span>
            </motion.div>
          );
        })}
      </div>
    </Stage>
  );
}

export const DEMOS = {
  "compare-prices": CompareDemo,
  "ai-search": SearchDemo,
  "virtual-try-on": TryOnDemo,
  "live-selling": LiveDemo,
  crosslist: CrosslistDemo,
  "auto-delist": DelistDemo,
  "ai-photoshoots": PhotoDemo,
  marketplaces: MarketsDemo,
  "graph-api": GraphDemo,
  "affiliate-rails": ClickDemo,
  "product-graph": GraphDemo,
  "all-capabilities": AllDemo,
};
